import { AssistantMessage } from "./types";

interface AssistantAuth {
  userType?: string;
  permissions?: string[] | string;
  isOwner?: boolean;
}

export interface AssistantExperience {
  brandName: "The Tradesmen's Collective" | "RELiBUILD";
  roleLabel: string;
  welcomeMessage: AssistantMessage;
}

const CONTRACTOR_FEATURES: Array<{ label: string; permissions?: string[] }> = [
  { label: "dashboard", permissions: ["View Dashboard", "VIEW_DASHBOARD"] },
  { label: "leads", permissions: ["VIEW_JOBS"] },
  { label: "contracts and milestones", permissions: ["VIEW_CONTRACT"] },
  { label: "team management", permissions: ["VIEW_COMPANY_STAFF", "MANAGE_COMPANY_STAFF", "MANAGE_TEAM"] },
  { label: "invoices", permissions: ["MANAGE_BILLING"] },
  { label: "chat", permissions: ["CHAT_ACCESS"] },
  { label: "merchant enrollment, KYC, payments, and insurance" },
  { label: "profile settings" },
];

const ROLE_FEATURES: Record<string, string[]> = {
  admin: [
    "dashboard and billing alerts", "contractors, homeowners, admins, and real-estate agents",
    "contracts, leads, escrow requests, and property leads", "merchant enrollment, KYC, payments, and payouts",
    "MLS cache, categories, coupons, versions, notifications, support, and Logan administration",
  ],
  homeowner: [
    "dashboard and profile", "projects and contracts", "recent and suggested contractors",
    "recommended properties and property management", "chat and support",
  ],
  subcontractor: ["dashboard and profile", "assigned contracts and project details", "support"],
  inspector: ["dashboard and profile", "contract review and project details", "support"],
  real_estate_agent: ["dashboard and profile", "property listings and property leads", "support"],
  real_esate_agent: ["dashboard and profile", "property listings and property leads", "support"],
  broker: ["dashboard and profile", "property listings and property leads", "support"],
};

const ROLE_LABELS: Record<string, string> = {
  admin: "administrator",
  contractor: "contractor",
  testcontractor: "contractor",
  homeowner: "homeowner",
  subcontractor: "subcontractor",
  inspector: "inspector",
  real_estate_agent: "real-estate agent",
  real_esate_agent: "real-estate agent",
  broker: "broker",
};

const ROLE_SUGGESTIONS: Record<string, string[]> = {
  admin: ["Show me the admin areas I can manage", "How do I manage Logan guidance?"],
  contractor: ["How do I create a contract?", "How do team permissions work?"],
  testcontractor: ["How do I create a contract?", "How do team permissions work?"],
  homeowner: ["How do I manage my projects?", "Where can I find suggested contractors?"],
  subcontractor: ["Where can I review my contracts?", "What can I update in my profile?"],
  inspector: ["Where can I review a contract?", "What can I do on the dashboard?"],
  real_estate_agent: ["Where can I view property leads?", "How do I update my profile?"],
  real_esate_agent: ["Where can I view property leads?", "How do I update my profile?"],
  broker: ["Where can I view property leads?", "How do I update my profile?"],
};

export function getAssistantExperience(auth: AssistantAuth | null | undefined): AssistantExperience {
  const userType = String(auth?.userType || "").trim().toLowerCase();
  const isContractor = userType === "contractor" || userType === "testcontractor";
  const brandName = isContractor ? "The Tradesmen's Collective" : "RELiBUILD";
  const roleLabel = ROLE_LABELS[userType] || "user";
  const roleArticle = /^[aeiou]/i.test(roleLabel) ? "an" : "a";
  const features = isContractor ? getContractorFeatures(auth) : (ROLE_FEATURES[userType] || ["dashboard", "profile", "support"]);
  const featureText = formatList(features);

  return {
    brandName,
    roleLabel,
    welcomeMessage: {
      id: "welcome",
      role: "assistant",
      message: `Hi! I’m Logan, your ${brandName} AI Product Guide. As ${roleArticle} ${roleLabel}, I can help you with ${featureText}. What would you like to do?`,
      suggestions: ROLE_SUGGESTIONS[userType] || ["What can I do on this page?", "How do I create a support ticket?"],
    },
  };
}

function getContractorFeatures(auth: AssistantAuth | null | undefined) {
  const rawPermissions = auth?.permissions;
  const permissions = Array.isArray(rawPermissions)
    ? rawPermissions
    : typeof rawPermissions === "string"
      ? [rawPermissions]
      : [];
  const hasFullAccess = auth?.isOwner === true || permissions.includes("ALL");

  return CONTRACTOR_FEATURES
    .filter((feature) => !feature.permissions || hasFullAccess || feature.permissions.some((permission) => permissions.includes(permission)))
    .map((feature) => feature.label);
}

function formatList(items: string[]) {
  if (items.length <= 1) return items[0] || "the pages available to your account";
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
